import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Shield, CheckCircle, AlertCircle } from 'lucide-react';
import './AdminRoleFix.css';

export function AdminRoleFix() {
  const { user } = useAuth();
  const [isFixing, setIsFixing] = useState(false);
  const [result, setResult] = useState<'success' | 'error' | null>(null);
  const [message, setMessage] = useState('');

  const fixAdminRole = async () => {
    if (!user) return;

    try {
      setIsFixing(true);
      setResult(null);
      setMessage('');

      // Update the user document with admin role
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        role: 'admin',
        displayName: user.displayName || 'Administrator',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      }, { merge: true });

      setResult('success');
      setMessage('Admin role assigned successfully! Please refresh the page.');
      
      // Auto-refresh after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error: any) {
      setResult('error');
      setMessage(error.message || 'Failed to update user role');
    } finally {
      setIsFixing(false);
    }
  };

  // Check if user has a valid role (admin or reviewer)
  const hasValidRole = user?.role === 'admin' || user?.role === 'viewer';
  
  if (!user || hasValidRole) {
    return null;
  }

  return (
    <div className="admin-role-fix">
      <div className="fix-card">
        <div className="fix-header">
          <Shield className="icon" />
          <h3>Role Assignment Required</h3>
        </div>
        
        <div className="fix-content">
          <p>
            Your account needs to be assigned a role to access the application.
            Click the button below to assign yourself administrator privileges.
          </p>
          
          {result && (
            <div className={`result-message ${result}`}>
              {result === 'success' ? (
                <CheckCircle className="icon" />
              ) : (
                <AlertCircle className="icon" />
              )}
              <span>{message}</span>
            </div>
          )}
          
          <button
            onClick={fixAdminRole}
            disabled={isFixing}
            className="fix-button"
          >
            <Shield className="icon" />
            {isFixing ? 'Assigning Admin Role...' : 'Assign Admin Role'}
          </button>
        </div>
      </div>
    </div>
  );
}
